import { useEffect, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type Country = components["schemas"]["CountryBankProfileSummary"];

export function CountriesTable() {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchCountries() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/v1/bank-details/countries");
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load countries.");
      } else {
        setCountries(data ?? []);
      }
      setLoading(false);
    }
    fetchCountries();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        {loading && <Spinner className="size-5" />}
        {!loading && (
          <span className="text-sm text-muted-foreground">
            {countries.length} {countries.length === 1 ? "country" : "countries"}
          </span>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Default Currencies</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {countries.length === 0 && !loading ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="text-muted-foreground h-24 text-center"
                >
                  No countries found.
                </TableCell>
              </TableRow>
            ) : (
              countries.map((country) => (
                <TableRow key={country.countryCode}>
                  <TableCell>
                    <Badge variant="secondary" className="font-mono text-xs">
                      {country.countryCode}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">
                    {country.countryName}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {country.defaultCurrencies.length === 0 ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        country.defaultCurrencies.map((c) => (
                          <Badge
                            key={c}
                            variant="outline"
                            className="font-mono text-xs"
                          >
                            {c}
                          </Badge>
                        ))
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
